import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import {
  Bell,
  ChevronRight,
  CircleHelp,
  FileText,
  Globe,
  History,
  Info,
  Mail,
  Megaphone,
  Mic,
  Scale,
  ShieldCheck,
} from 'lucide-react';

import { PRIVACY_URL, isRunningInNativeApp, native } from '@muzly/shared';
import type { AppVersion } from '@muzly/shared';

import { AvatarProfileIcon, Card, TransitionLink } from '../../components';
import { readItem, writeItem } from '../../core';
import { loadProgress, overallScore, useDiagnoses } from '../onboarding/record';
import { loadProfile, loadProfileImage } from '../profile';

/**
 * 마이페이지(더보기 탭) — 프로필 카드 · 활동 요약 · 설정 목록.
 *
 * 시안에 없는 화면이다(매니페스트 47개 중 0). 홈·결과 상세와 같은 카드 톤으로 전형적인
 * 모바일 마이페이지를 그린다: 위에 **누구인지**(사진·닉네임·취향 칩), 그 아래 **얼마나
 * 했는지**(진단 횟수·최근 점수·음역), 그리고 그룹별 목록(내 활동 · 설정 · 고객 지원 ·
 * 약관 및 정책 · 앱 정보).
 *
 * - 방침은 URL이 있으면 인앱 웹뷰(네이티브)·새 탭(브라우저)으로 열고, 없으면 앱 안의
 *   자리표시자 문서(`/more/docs/privacy`)로 간다 — 빈 항목을 두지 않는다.
 * - 버전은 웹 번들 버전(`__APP_VERSION__`, vite define)이 바닥이고, 네이티브 셸 안이면
 *   앱 버전을 물어 같이 적는다. 물음이 실패하면 조용히 웹 버전만 남는다.
 * - 알림 토글은 **이 기기의 설정**이다(localStorage). 푸시 구독을 실제로 바꾸는 배선은
 *   아직 없다 — 값만 기억한다.
 */

/** vite `define` — `apps/web/vite.config.mts`가 package.json 버전을 박는다. */
declare const __APP_VERSION__: string;

const NOTIFICATIONS_KEY = 'muzly.settings.notifications';

/**
 * 목록 행 하나 — 36px 회색 원 아이콘 · 라벨 · 오른쪽(값·화살표·스위치).
 *
 * 링크는 전부 `TransitionLink`다: 여기서 가는 곳(프로필·이력·진단·문서)은 모두 탭바를
 * 감추는 드릴다운이라 스택 전환을 타야 하고, 나올 때 헤더의 뒤로가기가 같은 전환을
 * 되감는다. 들어갈 때만 전환이 없으면 나올 때의 슬라이드가 혼자 튄다.
 */
const MenuRow = ({
  icon,
  label,
  right,
  to,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  right?: ReactNode;
  to?: string;
  onClick?: () => void;
}) => {
  const body = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bk-50 text-bk-800">
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-[15px] font-medium tracking-[-0.075px] text-bk-900">
        {label}
      </span>
      {right ?? (
        <ChevronRight className="h-5 w-5 text-bk-400" aria-hidden="true" />
      )}
    </>
  );
  const className =
    'flex min-h-[56px] w-full items-center gap-3.5 px-4 py-2.5 text-left';
  if (to) {
    return (
      <TransitionLink to={to} className={className}>
        {body}
      </TransitionLink>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
};

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="mt-7">
    <h2 className="mb-2 px-1 text-[13px] font-semibold tracking-[-0.065px] text-bk-700">
      {title}
    </h2>
    <Card className="divide-y divide-bk-50 overflow-hidden rounded-[20px]">
      {children}
    </Card>
  </section>
);

const Switch = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={cn(
      'relative h-[30px] w-[50px] shrink-0 rounded-full transition-colors',
      checked ? 'bg-primary' : 'bg-bk-300',
    )}
  >
    <span
      className={cn(
        'absolute left-0 top-[3px] h-6 w-6 rounded-full bg-white shadow-chip transition-transform',
        checked ? 'translate-x-[23px]' : 'translate-x-[3px]',
      )}
    />
  </button>
);

const iconClass = 'h-[18px] w-[18px]';

export const MorePage = ({
  privacyUrl = PRIVACY_URL,
}: {
  /** 테스트가 env 없이 두 상태를 보려고 주입한다. 기본은 env. */
  privacyUrl?: string;
}) => {
  const { t, i18n } = useTranslation();
  const [nativeVersion, setNativeVersion] = useState<AppVersion | null>(null);
  // 프로필·사진은 화면을 열 때 한 번만 읽는다(홈과 같은 관례). 회차(`records`)는
  // **구독형**(`useDiagnoses`)이다 — 부팅 직후 서버 이력 복원이 비동기로 끝나므로,
  // 이 화면이 열려 있는 동안 복원이 끝나도 다시 그려야 한다.
  const [profile] = useState(loadProfile);
  const records = useDiagnoses();
  const [image] = useState(loadProfileImage);
  // 진행 중인 회차가 있나 — 화면을 열 때 한 번. 진단 진입 목적지를 가른다.
  const [hasProgress] = useState(() => loadProgress() !== null);
  const [notifications, setNotifications] = useState(
    () => readItem(NOTIFICATIONS_KEY) !== 'off',
  );

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }
    native
      .invoke('getAppVersion')
      .then(setNativeVersion)
      .catch(() => {
        /* 브리지가 답하지 않으면 웹 버전만 보인다 — 화면이 멈출 이유는 아니다. */
      });
  }, []);

  const openPrivacy = (): void => {
    if (isRunningInNativeApp()) {
      void native.invoke('openInAppWeb', {
        url: privacyUrl,
        title: t('web.more.privacy'),
      });
      return;
    }
    window.open(privacyUrl, '_blank', 'noopener');
  };

  const toggleNotifications = (next: boolean): void => {
    setNotifications(next);
    writeItem(NOTIFICATIONS_KEY, next ? 'on' : 'off');
  };

  const answers = profile?.answers ?? {};
  const nick =
    typeof answers.nick === 'string' && answers.nick.trim()
      ? answers.nick
      : t('web.more.guest');
  // 취향 칩 — 답이 있는 것만. 설문 문구를 그대로 쓴다(같은 값이 다른 말이 되면 안 된다).
  const chips = (['style', 'experience'] as const).flatMap((key) => {
    const value = answers[key];
    return typeof value === 'string'
      ? [t(`web.onboarding.${key}.options.${value}`)]
      : [];
  });

  const latest = records[0] ?? null;
  const latestScore = latest ? overallScore(latest) : null;
  const language = i18n.resolvedLanguage ?? i18n.language;

  const versionText = nativeVersion
    ? t('web.more.versionNative', {
        app: nativeVersion.version,
        build: nativeVersion.buildNumber,
        web: __APP_VERSION__,
      })
    : t('web.more.versionWeb', { web: __APP_VERSION__ });

  const stats = [
    {
      key: 'runs',
      value:
        records.length > 0
          ? t('web.more.stats.runsValue', { count: records.length })
          : t('web.more.stats.none'),
    },
    {
      key: 'latest',
      value:
        latestScore == null ? t('web.more.stats.none') : String(latestScore),
    },
    {
      key: 'range',
      value: latest?.range
        ? t('web.home.tiles.range', {
            low: latest.range.lowNote,
            high: latest.range.highNote,
          })
        : t('web.more.stats.none'),
    },
  ];

  return (
    <div className="flex flex-1 flex-col px-[18px] pb-8 pt-[calc(var(--safe-area-top,0px)+1rem)]">
      <h1 className="px-0.5 text-[22px] font-semibold tracking-[-0.11px] text-bk-900">
        {t('web.more.title')}
      </h1>

      {/* 프로필 카드 — 누르면 프로필 수정(드릴다운이라 스택 전환). 사진이 없으면 시안의 회색 아바타. */}
      <TransitionLink to="/more/profile" className="mt-4 block">
        <Card className="flex items-center gap-4 p-5">
          {image ? (
            <img
              src={image}
              alt=""
              className="h-16 w-16 shrink-0 rounded-full object-cover"
            />
          ) : (
            <AvatarProfileIcon
              className="h-16 w-16 shrink-0"
              aria-hidden="true"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xl font-semibold tracking-[-0.1px] text-bk-900">
              {nick}
            </p>
            {/* 칩은 링크 안의 **요약 텍스트**다 — 각자 갈 곳이 없고 누르면 프로필로
                간다. 칩마다 다른 곳으로 가는 것처럼 보이지 않게 하나로 읽힌다. */}
            {chips.length > 0 && (
              <div
                aria-label={chips.join(', ')}
                className="mt-1.5 flex flex-wrap gap-1.5"
              >
                {chips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full bg-bk-50 px-2.5 py-1 text-xs font-medium text-bk-700"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-2 text-[13px] font-medium text-primary">
              {t('web.more.profile')}
            </p>
          </div>
          <ChevronRight
            className="h-5 w-5 shrink-0 text-bk-400"
            aria-hidden="true"
          />
        </Card>
      </TransitionLink>

      <Card className="mt-3 grid grid-cols-3 divide-x divide-bk-50 py-4">
        {stats.map(({ key, value }) => (
          <div key={key} className="flex flex-col items-center gap-1 px-2">
            <span className="text-xs text-bk-700">
              {t(`web.more.stats.${key}`)}
            </span>
            <span className="whitespace-nowrap text-lg font-bold tabular-nums text-bk-900">
              {value}
            </span>
          </div>
        ))}
      </Card>

      <Group title={t('web.more.groups.activity')}>
        <MenuRow
          icon={<History className={iconClass} />}
          label={t('web.more.history')}
          to="/history"
        />
        {/* 진행 중인 회차가 있으면 **홈으로** 보낸다 — `/diagnosis` 직행은 이어하기
            팝업(홈에 산다)을 건너뛰어, 반쯤 한 회차 한가운데로 떨어뜨렸다. */}
        <MenuRow
          icon={<Mic className={iconClass} />}
          label={t('web.more.items.diagnose')}
          to={hasProgress ? '/' : '/diagnosis'}
        />
      </Group>

      <Group title={t('web.more.groups.settings')}>
        <MenuRow
          icon={<Globe className={iconClass} />}
          label={t('web.more.items.language')}
          // 프로필 화면의 **언어 줄로** 보낸다 — 그냥 `/more/profile` 이면 도착해서
          // 언어를 다시 찾아야 한다(이 줄을 누른 사람은 이미 언어를 고르러 왔다).
          to="/more/profile#language"
          right={
            <span className="flex items-center gap-1 text-sm text-bk-600">
              {t(`web.language.${language}`)}
              <ChevronRight
                className="h-5 w-5 text-bk-400"
                aria-hidden="true"
              />
            </span>
          }
        />
        <MenuRow
          icon={<Bell className={iconClass} />}
          label={t('web.more.items.notificationsPending')}
          right={
            <Switch
              checked={notifications}
              onChange={toggleNotifications}
              label={t('web.more.items.notifications')}
            />
          }
        />
      </Group>

      <Group title={t('web.more.groups.support')}>
        <MenuRow
          icon={<Megaphone className={iconClass} />}
          label={t('web.more.items.notice')}
          to="/more/docs/notice"
        />
        <MenuRow
          icon={<CircleHelp className={iconClass} />}
          label={t('web.more.items.faq')}
          to="/more/docs/faq"
        />
        <MenuRow
          icon={<Mail className={iconClass} />}
          label={t('web.more.items.contact')}
          to="/more/docs/contact"
        />
      </Group>

      <Group title={t('web.more.groups.legal')}>
        <MenuRow
          icon={<FileText className={iconClass} />}
          label={t('web.more.items.terms')}
          to="/more/docs/terms"
        />
        {privacyUrl ? (
          <MenuRow
            icon={<ShieldCheck className={iconClass} />}
            label={t('web.more.privacy')}
            onClick={openPrivacy}
          />
        ) : (
          <MenuRow
            icon={<ShieldCheck className={iconClass} />}
            label={t('web.more.privacy')}
            to="/more/docs/privacy"
          />
        )}
        <MenuRow
          icon={<Scale className={iconClass} />}
          label={t('web.more.items.licenses')}
          to="/more/docs/licenses"
        />
      </Group>

      <Group title={t('web.more.groups.app')}>
        <MenuRow
          icon={<Info className={iconClass} />}
          label={t('web.more.version')}
          right={
            <span
              data-testid="app-version"
              className="text-sm tabular-nums text-bk-600"
            >
              {versionText}
            </span>
          }
        />
      </Group>
    </div>
  );
};
